export const heroContent = {
  eyebrow: 'Automated Statement Processing',
  title: 'Turn PDF Bank Statements into Clean, Structured Data.',
  description: 'BankStatementReader extracts transactions from PDF bank statements using AI vision extraction, then exports the results to Excel, CSV, or JSON, with no manual retyping.',
  cta: {
    primary: { text: 'Start Free', to: '/sign-up' },
    secondary: { text: 'View Pricing', href: '#pricing' }
  },
  dataCards: [
    { title: 'Input', value: 'PDF', description: 'Digital or scanned statements' },
    { title: 'Exports', value: 'XLSX · CSV · JSON', description: 'Structured transaction tables' },
    { title: 'Engine', value: 'AI Vision', description: 'Vision-LLM transaction extraction' },
    { title: 'Free Tier', value: '5 / mo', description: 'Statements per month, no card' }
  ]
}

export const aboutContent = {
  title: 'Take the Manual Lift out of Statement Reconciliation',
  description: 'BankStatementReader replaces hours of manual retyping with automated parsing and structured exports, so your finance ops can focus on review instead of data entry.'
}

export const problemsContent = {
  sectionTitle: "What's Slowing Down Your Close Today",
  sectionDescription: 'Finance teams still rely on spreadsheets, shared inboxes, and manual copying to reconcile cash activity. These friction points create delays, introduce risk, and make audits painful.',
  cards: [
    {
      title: 'Fragmented Formats',
      description: 'Scanned PDFs, image exports, and multi-language statements land differently every time, forcing analysts to rebuild templates from scratch.'
    },
    {
      title: 'Hidden Exceptions',
      description: 'Manual spot checks miss duplicate charges, reversals, and suspicious activity that demand real-time alerting.'
    },
    {
      title: 'Compliance Pressure',
      description: 'AUD, PCI, and FINTRAC require clean evidence trails. Manually stitching documents creates audit gaps and stress.'
    }
  ]
}

export const featuresContent = {
  sectionTitle: 'From PDF to Spreadsheet, Without the Retyping',
  sectionDescription: 'Upload a statement, get a structured transaction table, and export it in the format your workflow needs. Low-confidence results are flagged so you know what to double-check.',
  cards: [
    {
      title: 'Upload and Extract',
      description: 'Drag and drop a PDF statement, digital or scanned. AI vision extraction reads each page and returns a consistent table: date, description, debit, credit, balance.',
      accent: true
    },
    {
      title: 'Flagged for Review',
      description: 'Low-confidence extractions are flagged in your dashboard, so you know exactly which statements to double-check against the original PDF.'
    },
    {
      title: 'Free Tools, In Your Browser',
      description: 'The free tools (converter demo, redaction, CSV cleanup and more) run entirely in your browser. Files you open there never leave your device.'
    },
    {
      title: 'Clean Exports',
      description: 'Download results as XLSX, CSV, or JSON, ready for Excel, Google Sheets, and import into accounting software.'
    }
  ]
}

export const pricingContent = {
  sectionTitle: 'Simple, Transparent Pricing',
  sectionDescription: 'Choose the plan that meets your statement volume. Every tier includes encryption in transit and at rest, and email support. Flat monthly pricing, no credits or tokens. One-off cleanup job? A one-time Backlog Pass ($39 for 200 statements) is available from your dashboard.',
  tiers: [
    {
      name: 'Solo',
      price: '$29',
      period: '/month',
      description: 'For freelancers and one-person books.',
      features: [
        'Up to 100 statements / month',
        'AI vision extraction, digital and scanned PDFs',
        'XLSX, CSV & JSON exports',
        'Email support'
      ],
      planId: 'solo',
      cta: { text: 'Start Free', to: '/sign-up' }
    },
    {
      name: 'Starter',
      price: '$79',
      period: '/month',
      description: 'Perfect for lean finance teams digitizing their first workflows.',
      features: [
        'Up to 500 statements / month',
        'AI vision extraction, digital and scanned PDFs',
        'XLSX, CSV & JSON exports',
        'Email support'
      ],
      planId: 'starter',
      cta: { text: 'Start Free', to: '/sign-up' }
    },
    {
      name: 'Growth',
      price: '$199',
      period: '/month',
      description: 'Higher volume for bookkeeping firms and multi-entity teams.',
      features: [
        'Up to 2,000 statements / month',
        'Everything in Starter',
        'Built for backlog and cleanup jobs',
        'Email support'
      ],
      planId: 'growth',
      cta: { text: 'Start Free', to: '/sign-up' },
      highlight: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: ' pricing',
      description: 'For teams with volume beyond Growth. Tell us what you need.',
      features: [
        'Custom statement volume',
        'Direct line to the team',
        'Onboarding help for your workflow',
        'Custom terms by arrangement'
      ],
      cta: { text: 'Talk to Sales', href: 'mailto:hello@bankstatementreader.com' }
    }
  ]
}

export const faqContent = {
  eyebrow: 'Everything You Need to Know',
  title: 'Frequently Asked Questions',
  description: 'From implementation timelines to security posture, here are the most common questions finance and compliance teams ask before rolling out BankStatementReader.',
  questions: [
    {
      question: 'How quickly can we get started?',
      answer: 'There is nothing to deploy. Sign up, upload a PDF statement, and download the extracted table. The free tier includes 5 statements a month with no card required.'
    },
    {
      question: 'Can you read image-only or scanned PDFs?',
      answer: 'Yes. The extraction engine reads both digital and scanned statement pages. Low-confidence pages are flagged so you can review them before exporting.'
    },
    {
      question: 'Where does my data live?',
      answer: 'Uploaded statements are stored encrypted at rest in our cloud object storage and transmitted over HTTPS. Statement page images are sent to a third-party AI provider solely to perform extraction, as described in our Privacy Policy. You can delete your statements from the dashboard at any time.'
    },
    {
      question: 'Do you offer human-in-the-loop review?',
      answer: 'Extraction is automated, and you stay in the loop: low-confidence results are flagged in your dashboard so you can verify them against the original statement before relying on the export.'
    },
    {
      question: 'What integrations are supported?',
      answer: 'Exports are standard XLSX, CSV, and JSON files that open in Excel and Google Sheets and import into most accounting software. We do not currently offer native ERP connectors or a public API.'
    }
  ]
}
